/**
 * Navigation Component
 * Main navigation bar with responsive design and user authentication
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES, ADMIN_ROUTES } from '../../config';
import { PAGE_KEYS } from '../../config/pageKeys';
import { useUserState } from '../../../features/user/operations/UserStateProvider';
import { useLogout } from '../../../features/user/operations/useLogout';
import { useSafeThemeClasses, ThemeToggle, SimpleThemeToggle } from '@/shared/theme';
import { ENV } from '../../../config/environment';
import { data } from '@/content/data';

export const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isFunToolsOpen, setIsFunToolsOpen] = useState(false);
  const location = useLocation();
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const funToolsRef = useRef<HTMLDivElement>(null);
  
  const { user, isAuthenticated, hasEntitlement } = useUserState();
  const { logout } = useLogout();
  const theme = useSafeThemeClasses();

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (funToolsRef.current && !funToolsRef.current.contains(event.target as Node)) {
        setIsFunToolsOpen(false);
      }
    };

    if (isProfileMenuOpen || isMenuOpen || isFunToolsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen, isMenuOpen, isFunToolsOpen]);

  const isActive = (path: string) => location.pathname === path;

  const isFunToolsRouteActive =
    location.pathname === ROUTES.TOASTMASTERS_SUITE || location.pathname === ROUTES.CALCULATOR;

  const navItems = [
    { label: data.navigation.projects, path: ROUTES.PROJECTS },
    { label: data.navigation.blog, path: ROUTES.BLOG },
    { label: data.navigation.about, path: ROUTES.ABOUT },
  ];

  const handleLogout = async () => {
    const success = await logout();
    setIsProfileMenuOpen(false);
    // Force refresh user state immediately
    if (success) {
      // Clear user state immediately
      window.location.href = ROUTES.LOGIN;
    }
  };

  const renderUserInitials = () => {
    if (!user) return 'U';
    
    // Try to get initials from first_name and last_name first
    if (user.first_name || user.last_name) {
      const first = user.first_name?.charAt(0) || '';
      const last = user.last_name?.charAt(0) || '';
      if (first || last) {
        return (first + last).toUpperCase() || 'U';
      }
    }
    
    // Fallback to username - get first letter of first word and first letter of last word
    if (user.username) {
      const parts = user.username.trim().split(' ').filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
      }
    }
    
    return 'U'; // Generic unidentified user
  };

  const renderAvatar = () => {
    return (
      <div className="relative" ref={profileMenuRef}>
        <button 
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${theme.buttonGhost} hover:scale-105`}
          onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsProfileMenuOpen(!isProfileMenuOpen);
            }
          }}
          aria-label="User menu"
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${theme.avatarButton}`}>
            {isAuthenticated && user?.avatar ? (
              <img 
                src={user.avatar} 
                alt={`${user.username}'s profile`}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <span className={`text-sm font-semibold ${theme.textPrimary}`}>
                {renderUserInitials()}
              </span>
            )}
          </div>
        </button>
        {isProfileMenuOpen && (
          <ul className={`absolute right-0 top-full mt-3 z-[1] p-2 rounded-box w-52 ${theme.navDropdown}`}>
            {isAuthenticated && user ? (
              <>
                <li className="px-4 py-2">
                  <div className={theme.separator}></div>
                  <span className={`text-xs ${theme.textMuted}`}>
                    {user.email}
                  </span>
                </li>
                {hasEntitlement(PAGE_KEYS.DASHBOARD) && (
                  <li className="w-full">
                    <Link
                      to={ROUTES.DASHBOARD}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${theme.navItem} ${theme.textPrimary}`}
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      {data.navigation.dashboard}
                    </Link>
                  </li>
                )}
                {(user?.is_admin || user?.is_god) && hasEntitlement(PAGE_KEYS.ADMIN_DASHBOARD) && (
                  <li className="w-full">
                    <Link
                      to={ADMIN_ROUTES.DASHBOARD}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${theme.navItem} ${theme.textPrimary}`}
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      {data.navigation.adminPanel}
                    </Link>
                  </li>
                )}
                {(user?.is_admin || user?.is_god) && hasEntitlement(PAGE_KEYS.ADMIN_FLASHCARDS) && (
                  <li className="w-full">
                    <Link
                      to={ADMIN_ROUTES.FLASHCARDS}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${theme.navItem} ${theme.textPrimary}`}
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      {data.navigation.adminFlashcards}
                    </Link>
                  </li>
                )}
                <li className="w-full">
                  <button 
                    onClick={handleLogout}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 w-full text-left text-error hover:bg-error/20 hover:scale-[1.02] active:scale-[0.98] hover:shadow-md hover:shadow-error/10 ${theme.navItem}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    {data.navigation.logout}
                  </button>
                </li>
              </>
            ) : (
              <>
                <li className="w-full">
                  <Link 
                    to={ROUTES.LOGIN}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${theme.navItem} ${theme.textPrimary}`}
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    {data.navigation.signIn}
                  </Link>
                </li>
              </>
            )}
          </ul>
        )}
      </div>
    );
  };

  return (
    <nav className={`flex items-center justify-between w-full sticky top-0 z-50 px-4 py-2 ${theme.navMain}`}>
      {/* Left Section - Mobile menu + Brand */}
      <div className="flex items-center gap-4">
        <div className="relative lg:hidden" ref={mobileMenuRef}>
          <button 
            className={`p-2 rounded-lg transition-all duration-200 ${theme.buttonGhost} hover:scale-105`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Mobile menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h8m-8 6h16" />
            </svg>
          </button>
          {isMenuOpen && (
            <ul className={`absolute left-0 top-full mt-3 z-[1] p-2 rounded-box w-52 ${theme.navDropdown}`}>
              {navItems.map((item) => (
                <li key={item.path} className="w-full">
                  <Link 
                    to={item.path}
                    className={`block px-4 py-2 rounded-lg transition-all duration-200 ${theme.textPrimary} ${isActive(item.path) ? theme.navItemActive : theme.navItem}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              <div className={theme.separator} />
              <li className="w-full">
                <SimpleThemeToggle />
              </li>
              {isAuthenticated && (
                <>
                  {(hasEntitlement(PAGE_KEYS.TOASTMASTERS) || hasEntitlement(PAGE_KEYS.CALCULATOR)) && (
                    <>
                      <div className={theme.separator} />
                      <li className={`px-4 py-1 text-xs font-semibold tracking-wide ${theme.textMuted}`}>
                        {data.navigation.funTools}
                      </li>
                      {hasEntitlement(PAGE_KEYS.TOASTMASTERS) && (
                        <li className="w-full">
                          <Link
                            to={ROUTES.TOASTMASTERS_SUITE}
                            className={`block px-4 py-2 rounded-lg transition-all duration-200 ${theme.textPrimary} ${
                              isActive(ROUTES.TOASTMASTERS_SUITE) ? theme.navItemActive : theme.navItem
                            }`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {data.navigation.funToolsToastmasters}
                          </Link>
                        </li>
                      )}
                      {hasEntitlement(PAGE_KEYS.CALCULATOR) && (
                        <li className="w-full">
                          <Link
                            to={ROUTES.CALCULATOR}
                            className={`block px-4 py-2 rounded-lg transition-all duration-200 ${theme.textPrimary} ${
                              isActive(ROUTES.CALCULATOR) ? theme.navItemActive : theme.navItem
                            }`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {data.navigation.funToolsLivingExpense}
                          </Link>
                        </li>
                      )}
                    </>
                  )}
                  {(user?.is_admin || user?.is_god) &&
                    (hasEntitlement(PAGE_KEYS.ADMIN_DASHBOARD) || hasEntitlement(PAGE_KEYS.ADMIN_FLASHCARDS)) && (
                    <>
                      <div className={theme.separator} />
                      <li className={`px-4 py-1 text-xs font-semibold tracking-wide ${theme.textMuted}`}>
                        Admin
                      </li>
                      {hasEntitlement(PAGE_KEYS.ADMIN_DASHBOARD) && (
                        <li className="w-full">
                          <Link
                            to={ADMIN_ROUTES.DASHBOARD}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${theme.navItem} ${theme.textPrimary}`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {data.navigation.adminPanel}
                          </Link>
                        </li>
                      )}
                      {hasEntitlement(PAGE_KEYS.ADMIN_FLASHCARDS) && (
                        <li className="w-full">
                          <Link
                            to={ADMIN_ROUTES.FLASHCARDS}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${theme.navItem} ${theme.textPrimary}`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {data.navigation.adminFlashcards}
                          </Link>
                        </li>
                      )}
                    </>
                  )}
                  {hasEntitlement(PAGE_KEYS.BLOG_CREATE) && (
                    <>
                      <div className={theme.separator} />
                      <li className="w-full">
                        <Link
                          to={ROUTES.BLOG_CREATE}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          {data.navigation.createBlog}
                        </Link>
                      </li>
                    </>
                  )}
                </>
              )}
            </ul>
          )}
        </div>
        <Link 
          to={ROUTES.HOME} 
          className={`text-xl font-bold hover:scale-105 transition-transform duration-200 ${theme.textHeading}`}
        >
          { ENV.project.name } 
        </Link>
      </div>
      
      {/* Center Section - Desktop navigation */}
      <div className="hidden lg:flex items-center gap-2 xl:gap-6 2xl:gap-8">
        {navItems.map((item) => (
          <Link 
            key={item.path}
            to={item.path}
            className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${theme.textPrimary} ${
              isActive(item.path) 
                ? theme.navItemActive
                : theme.navItem
            }`}
          >
            {item.label}
          </Link>
        ))}
        {isAuthenticated && (hasEntitlement(PAGE_KEYS.TOASTMASTERS) || hasEntitlement(PAGE_KEYS.CALCULATOR)) && (
          <div className="relative" ref={funToolsRef}>
            <button
              type="button"
              onClick={() => {
                setIsFunToolsOpen((open) => !open);
                setIsProfileMenuOpen(false);
              }}
              className={`flex items-center gap-1 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${theme.textPrimary} ${
                isFunToolsRouteActive || isFunToolsOpen ? theme.navItemActive : theme.navItem
              }`}
              aria-expanded={isFunToolsOpen}
              aria-haspopup="menu"
            >
              {data.navigation.funTools}
              <svg
                className={`${theme.navFunToolsChevron} ${isFunToolsOpen ? theme.navFunToolsChevronOpen : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isFunToolsOpen && (
              <ul
                className={`absolute left-0 top-full z-20 mt-2 min-w-[15rem] py-2 ${theme.navDropdown}`}
                role="menu"
              >
                {hasEntitlement(PAGE_KEYS.TOASTMASTERS) && (
                  <li role="none">
                    <Link
                      role="menuitem"
                      to={ROUTES.TOASTMASTERS_SUITE}
                      className={`flex px-4 py-2 rounded-lg ${theme.navItem} ${theme.textPrimary} ${
                        isActive(ROUTES.TOASTMASTERS_SUITE) ? theme.navItemActive : ''
                      }`}
                      onClick={() => setIsFunToolsOpen(false)}
                    >
                      {data.navigation.funToolsToastmasters}
                    </Link>
                  </li>
                )}
                {hasEntitlement(PAGE_KEYS.CALCULATOR) && (
                  <li role="none">
                    <Link
                      role="menuitem"
                      to={ROUTES.CALCULATOR}
                      className={`flex px-4 py-2 rounded-lg ${theme.navItem} ${theme.textPrimary} ${
                        isActive(ROUTES.CALCULATOR) ? theme.navItemActive : ''
                      }`}
                      onClick={() => setIsFunToolsOpen(false)}
                    >
                      {data.navigation.funToolsLivingExpense}
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>
      
      {/* Right Section - Create Blog Button, Theme Toggle, and Avatar */}
      <div className="flex items-center gap-2">
        {/* Create Blog Button - Only visible to authenticated users */}
        {isAuthenticated && hasEntitlement(PAGE_KEYS.BLOG_CREATE) && (
          <Link
            to={ROUTES.BLOG_CREATE}
            className={`hidden lg:flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200 text-sm font-semibold ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:scale-105`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {data.navigation.createBlog}
          </Link>
        )}
        <div className="hidden shrink-0 items-center lg:flex">
          <ThemeToggle />
        </div>
        {renderAvatar()}
      </div>
    </nav>
  );
};

export default Navigation;
